import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Controller, useForm } from 'react-hook-form';
import { Divider, MenuItem, Select, TextField, TextButton, useOverlayContext } from 'seagull';
import { CloseCross, PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateContact } from '~/business-logic/validators';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { UserRole } from '~/models';
import { useStoreActions } from '~/store/store-context';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './InviteTeamStep.module.scss';
export const GetInviteTeamStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const inviteTeamStepTextsPath = 'vendorOnboarding.inviteTeamStep';
const validateEmail = (value, emailRequiredText, emailInvalidText)=>{
    if (value.trim().length === 0) {
        return emailRequiredText;
    }
    const result = validateContact.email(value.trim());
    if (typeof result === 'string') {
        return emailInvalidText;
    }
    return true;
};
const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 30;
const NAME_DENY_PATTERN = /[=<>]/;
const validateName = (value, requiredText, invalidText)=>{
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return requiredText;
    }
    if (trimmed.length < MIN_NAME_LENGTH || trimmed.length > MAX_NAME_LENGTH || NAME_DENY_PATTERN.test(trimmed)) {
        return invalidText;
    }
    return true;
};
const ROLE_OPTIONS = [
    {
        value: UserRole.Admin,
        labelKey: 'roleAdmin'
    },
    {
        value: UserRole.Member,
        labelKey: 'roleMember'
    }
];
const memberRowVariants = {
    initial: {
        opacity: 0,
        y: -8
    },
    animate: {
        opacity: 1,
        y: 0
    },
    exit: {
        opacity: 0,
        y: -8
    }
};
let nextMemberId = 1;
export const createEmptyMember = ()=>({
        id: nextMemberId++,
        firstName: '',
        lastName: '',
        email: '',
        role: ''
    });
const isMemberEmpty = (member)=>member.firstName.trim() === '' && member.lastName.trim() === '' && member.email.trim() === '' && member.role === '';
const areAllMembersEmpty = (members)=>members.every(isMemberEmpty);
export const InviteTeamStep = (param)=>{
    let { testId, values, onChange, onContinue, submitRef, onSubmittingChange } = param;
    var _watch;
    const testIds = useMemo(()=>GetInviteTeamStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(inviteTeamStepTextsPath);
    const { api, logBI } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isInitialRender = useRef(true);
    useEffect(()=>{
        isInitialRender.current = false;
    }, []);
    const { control, handleSubmit, watch, setValue, getValues, setError } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            members: values.members
        }
    });
    const memberRowRefs = useRef([]);
    const previousMembersCount = useRef(values.members.length);
    useEffect(()=>{
        if (values.members.length > previousMembersCount.current) {
            const lastRow = memberRowRefs.current[values.members.length - 1];
            lastRow === null || lastRow === void 0 ? void 0 : lastRow.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
        previousMembersCount.current = values.members.length;
    }, [
        values.members.length
    ]);
    const previousIdsRef = useRef(values.members.map((m)=>m.id).join(','));
    useEffect(()=>{
        var _getValues;
        const newIds = values.members.map((m)=>m.id).join(',');
        if (newIds === previousIdsRef.current) return;
        const formMembers = (_getValues = getValues('members')) !== null && _getValues !== void 0 ? _getValues : [];
        const merged = values.members.map((parentMember)=>{
            const formMember = formMembers.find((f)=>(f === null || f === void 0 ? void 0 : f.id) === parentMember.id);
            return formMember !== null && formMember !== void 0 ? formMember : parentMember;
        });
        setValue('members', merged);
        previousIdsRef.current = newIds;
    }, [
        values.members,
        setValue,
        getValues
    ]);
    const watched = (_watch = watch('members')) !== null && _watch !== void 0 ? _watch : [];
    useEffect(()=>{
        if (watched.length !== values.members.length) return;
        if (!watched.every((m, i)=>(m === null || m === void 0 ? void 0 : m.id) === values.members[i].id)) return;
        const merged = watched.map((w, i)=>_object_spread({}, values.members[i], w));
        if (JSON.stringify(merged) !== JSON.stringify(values.members)) {
            onChange({
                members: merged
            });
        }
    }, [
        watched,
        values.members,
        onChange
    ]);
    useEffect(()=>{
        onSubmittingChange === null || onSubmittingChange === void 0 ? void 0 : onSubmittingChange(isSubmitting);
    }, [
        isSubmitting,
        onSubmittingChange
    ]);
    const addMember = useCallback(()=>{
        var _getValues;
        const newMember = createEmptyMember();
        const formMembers = (_getValues = getValues('members')) !== null && _getValues !== void 0 ? _getValues : [];
        onChange((prev)=>{
            const merged = prev.members.map((parentMember)=>{
                const formMember = formMembers.find((f)=>(f === null || f === void 0 ? void 0 : f.id) === parentMember.id);
                return formMember !== null && formMember !== void 0 ? formMember : parentMember;
            });
            return _object_spread_props(_object_spread({}, prev), {
                members: [
                    ...merged,
                    newMember
                ]
            });
        });
    }, [
        getValues,
        onChange
    ]);
    const handleRemove = useCallback((index)=>{
        var _getValues;
        const formMembers = (_getValues = getValues('members')) !== null && _getValues !== void 0 ? _getValues : [];
        onChange((prev)=>{
            const merged = prev.members.map((parentMember)=>{
                const formMember = formMembers.find((f)=>(f === null || f === void 0 ? void 0 : f.id) === parentMember.id);
                return formMember !== null && formMember !== void 0 ? formMember : parentMember;
            });
            return _object_spread_props(_object_spread({}, prev), {
                members: merged.filter((_, i)=>i !== index)
            });
        });
    }, [
        getValues,
        onChange
    ]);
    const routeInviteError = useCallback((error, memberIndex)=>{
        const e = new ErrorResponse(error);
        const code = e.validationErrorCode();
        if (code === 'name-firstname') {
            setError("members.".concat(memberIndex, ".firstName"), {
                message: t.firstNameInvalid()
            });
            return;
        }
        if (code === 'name-lastname') {
            setError("members.".concat(memberIndex, ".lastName"), {
                message: t.lastNameInvalid()
            });
            return;
        }
        showErrorToast(t.submitError(), testIds.InviteTeamStepTestIds.SubmitError());
    }, [
        setError,
        showErrorToast,
        t,
        testIds
    ]);
    const onValidSubmit = useCallback(async (data)=>{
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            const parentById = new Map(values.members.map((m)=>[
                    m.id,
                    m
                ]));
            const isAlreadyInvited = (m)=>{
                var _parentById_get;
                return Boolean((_parentById_get = parentById.get(m.id)) === null || _parentById_get === void 0 ? void 0 : _parentById_get.invited);
            };
            const alreadyInvitedEmails = new Set(data.members.filter(isAlreadyInvited).map((m)=>m.email.trim().toLowerCase()));
            for(let memberIndex = 0; memberIndex < data.members.length; memberIndex++){
                const member = data.members[memberIndex];
                const parent = parentById.get(member.id);
                const newRole = member.role;
                const invitedUserId = parent === null || parent === void 0 ? void 0 : parent.invitedUserId;
                const invitedRole = parent === null || parent === void 0 ? void 0 : parent.invitedRole;
                if (invitedUserId && invitedRole && invitedRole !== newRole) {
                    try {
                        await api.member.changeRole({
                            id: invitedUserId,
                            currentRole: invitedRole,
                            newRole
                        });
                    } catch (error) {
                        routeInviteError(error, memberIndex);
                        return;
                    }
                    onChange((prev)=>_object_spread_props(_object_spread({}, prev), {
                            members: prev.members.map((m)=>m.id === member.id ? _object_spread_props(_object_spread({}, m), {
                                    invitedRole: newRole
                                }) : m)
                        }));
                    continue;
                }
                if (parent === null || parent === void 0 ? void 0 : parent.invited) continue;
                const email = member.email.trim().toLowerCase();
                if (alreadyInvitedEmails.has(email)) continue;
                let invitedId;
                try {
                    const result = await api.member.invite({
                        firstName: member.firstName.trim(),
                        lastName: member.lastName.trim(),
                        email: member.email.trim(),
                        role: newRole
                    });
                    invitedId = result === null || result === void 0 ? void 0 : result.id;
                } catch (error) {
                    routeInviteError(error, memberIndex);
                    return;
                }
                alreadyInvitedEmails.add(email);
                onChange((prev)=>_object_spread_props(_object_spread({}, prev), {
                        members: prev.members.map((m)=>m.id === member.id ? _object_spread_props(_object_spread({}, m), {
                                invited: true,
                                invitedUserId: invitedId,
                                invitedRole: newRole
                            }) : m)
                    }));
            }
            logBI(99999, {
                memberCount: data.members.length,
                action: 'continue'
            });
            onContinue();
        } finally{
            setIsSubmitting(false);
        }
    }, [
        api.member,
        isSubmitting,
        logBI,
        onChange,
        onContinue,
        routeInviteError,
        values.members
    ]);
    const triggerSubmit = useCallback(()=>{
        const currentMembers = watch('members');
        if (areAllMembersEmpty(currentMembers)) {
            onContinue();
            return;
        }
        handleSubmit(onValidSubmit)();
    }, [
        handleSubmit,
        onContinue,
        onValidSubmit,
        watch
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: triggerSubmit
        }), [
        triggerSubmit
    ]);
    const showRemoveButtons = values.members.length > 1;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.InviteTeamStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(AnimatePresence, {
                initial: false,
                children: values.members.map((member, index)=>/*#__PURE__*/ _jsxs(motion.div, {
                        ref: (el)=>{
                            memberRowRefs.current[index] = el;
                        },
                        variants: memberRowVariants,
                        initial: isInitialRender.current ? false : 'initial',
                        animate: "animate",
                        exit: "exit",
                        transition: {
                            duration: 0.25,
                            ease: 'easeOut'
                        },
                        className: styles.memberRow,
                        "data-testid": testIds.InviteTeamStepTestIds.MemberRow(),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.nameRow,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".firstName"),
                                            rules: {
                                                validate: (value)=>validateName(value, t.firstNameRequired(), t.firstNameInvalid())
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    bi: {
                                                        name: 'invite_team_first_name'
                                                    },
                                                    label: t.firstNameLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.FirstNameField(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".lastName"),
                                            rules: {
                                                validate: (value)=>validateName(value, t.lastNameRequired(), t.lastNameInvalid())
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    bi: {
                                                        name: 'invite_team_last_name'
                                                    },
                                                    label: t.lastNameLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.LastNameField(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)))
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.contactRow,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".email"),
                                            rules: {
                                                validate: (value)=>validateEmail(value, t.emailRequired(), t.emailInvalid())
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    bi: {
                                                        name: 'invite_team_email'
                                                    },
                                                    label: t.emailLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.EmailField(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".role"),
                                            rules: {
                                                validate: (v)=>v !== '' || t.roleRequired()
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                                    bi: {
                                                        name: 'invite_team_role'
                                                    },
                                                    label: t.roleLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.RoleSelect(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)), {
                                                    children: ROLE_OPTIONS.map((param)=>{
                                                        let { value, labelKey } = param;
                                                        return /*#__PURE__*/ _jsx(MenuItem, {
                                                            value: value,
                                                            label: t[labelKey](),
                                                            testId: "".concat(testIds.InviteTeamStepTestIds.RoleSelect(), "-").concat(index, "-option-").concat(value),
                                                            children: t[labelKey]()
                                                        }, value);
                                                    })
                                                }))
                                        })
                                    })
                                ]
                            }),
                            showRemoveButtons && /*#__PURE__*/ _jsx("div", {
                                className: styles.removeButton,
                                children: /*#__PURE__*/ _jsx(TextButton, {
                                    skin: "dark",
                                    underline: "none",
                                    variant: "paragraph2",
                                    prefixIcon: /*#__PURE__*/ _jsx(CloseCross, {
                                        size: 12
                                    }),
                                    onClick: ()=>handleRemove(index),
                                    testId: "".concat(testIds.InviteTeamStepTestIds.RemoveButton(), "-").concat(index),
                                    children: t.remove()
                                })
                            }),
                            index < values.members.length - 1 && /*#__PURE__*/ _jsx(Divider, {})
                        ]
                    }, member.id))
            }),
            /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx(TextButton, {
                    skin: "primary",
                    underline: "none",
                    variant: "paragraph2",
                    weight: "normal",
                    prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 14
                    }),
                    onClick: addMember,
                    testId: testIds.InviteTeamStepTestIds.AddAnotherButton(),
                    children: t.addAnother()
                })
            })
        ]
    });
};
